/**
 * Login page with email/password, MFA, passkey, and forgot-password support
 */
import { useState, useEffect, FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../services/auth'
import {
  authenticateWithPasskey,
  authenticateWithPasskeyConditional,
  isPasskeySupported,
} from '../services/passkeys'
import MfaSetupWizard from '../components/MfaSetupWizard'
import api from '../services/api'
import type { LoginResponse } from '../types'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, verifyMFA, refreshUser } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaSetup, setMfaSetup] = useState(false)
  const [sessionId, setSessionId] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)

  // Environment indicator (pre-auth). Mirrors the amber banner in Layout so a
  // non-production deployment is obvious before login too. /v1/version is public.
  const [environment, setEnvironment] = useState<string | null>(null)
  useEffect(() => {
    api
      .get<{ environment: string }>('/v1/version')
      .then((r) => setEnvironment(r.data.environment))
      .catch(() => setEnvironment(null))
  }, [])

  // Passkey autofill (conditional UI): if the browser supports it, quietly offer
  // any saved passkey for this site in the email field's autofill dropdown. The
  // ceremony only runs if the user actively picks a passkey; otherwise this is a
  // no-op and the normal password / "Sign in with a passkey" button still work.
  useEffect(() => {
    if (!isPasskeySupported()) return
    const controller = new AbortController()
    authenticateWithPasskeyConditional(controller.signal)
      .then(async (tokenResp) => {
        if (!tokenResp?.access_token) return
        api.defaults.headers.common['Authorization'] = `Bearer ${tokenResp.access_token}`
        await refreshUser()
        navigate('/dashboard')
      })
      .catch(() => {
        /* aborted or unavailable — fall back to the form silently */
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Forgot password state. Auto-open when arriving via /login?forgot=1 (e.g. from an
  // expired/used reset link's "Request a new link" button).
  const [searchParams] = useSearchParams()
  const [showForgotPassword, setShowForgotPassword] = useState(
    () => searchParams.get('forgot') === '1'
  )
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotMessage, setForgotMessage] = useState('')
  const [forgotError, setForgotError] = useState('')

  const handleForgotPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setForgotError('')
    setForgotMessage('')
    setForgotLoading(true)
    try {
      const resp = await api.post('/v1/auth/forgot-password', { email: forgotEmail })
      setForgotMessage(resp.data.message || 'Check your email for a reset link.')
    } catch {
      // The API always returns 200, so errors here are network/server faults
      setForgotError('Unable to send reset email. Please try again later.')
    } finally {
      setForgotLoading(false)
    }
  }

  const handleLoginSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response: LoginResponse = await login({ email, password })

      if (response.requires_mfa_setup && response.session_id) {
        // User has no MFA yet (e.g. freshly invited) — run first-login setup.
        setSessionId(response.session_id)
        setMfaSetup(true)
      } else if (response.requires_mfa && response.session_id) {
        setMfaRequired(true)
        setSessionId(response.session_id)
      } else {
        // Login successful, redirect to dashboard
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setError('')
    setPasskeyLoading(true)
    try {
      const tokenResp = await authenticateWithPasskey(email || undefined)
      if (tokenResp.access_token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${tokenResp.access_token}`
      }
      await refreshUser()
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey sign-in failed')
    } finally {
      setPasskeyLoading(false)
    }
  }

  const handleMFASubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await verifyMFA({
        session_id: sessionId,
        code: mfaCode,
        remember_device: rememberDevice,
        device_name: rememberDevice ? (navigator.userAgent.slice(0, 100) || undefined) : undefined,
      })
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA verification failed')
      setMfaCode('')
    } finally {
      setLoading(false)
    }
  }

  const showEnvBanner = !!environment && environment !== 'production'

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Environment banner — hidden in production (mirrors Layout).
          Deliberately in normal flow rather than `fixed`: fixed meant the card
          slid underneath it whenever the content was taller than the viewport,
          which on a landscape phone is always. */}
      {showEnvBanner && (
        <div
          className="bg-amber-400 text-amber-900 text-center text-xs font-bold py-1 px-4 tracking-wide"
          role="status"
          aria-label={`Running in ${environment} environment`}
        >
          {environment.toUpperCase()} — not production
        </div>
      )}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        {/* m-auto, not just the parent's items-center: a flex item taller than
            its container gets clipped at the top when centred. */}
        <div className="max-w-md w-full space-y-8 m-auto">
        <div className="flex flex-col items-center">
          <img src="/favicon.svg" alt="FCC License Manager" className="h-16 w-16" />
          <h1 className="mt-3 text-2xl font-bold text-gray-900 tracking-tight">
            FCC License Manager
          </h1>
          <h2 className="mt-4 text-center text-xl font-semibold text-gray-700">
            {showForgotPassword
              ? 'Reset your password'
              : mfaSetup
                ? 'Set up authentication'
                : mfaRequired
                  ? 'Enter MFA Code'
                  : 'Sign in to your account'}
          </h2>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* ===== Forgot Password panel ===== */}
        {showForgotPassword ? (
          <div className="mt-8 space-y-6">
            {forgotMessage ? (
              <div className="alert alert-success">
                {forgotMessage}
              </div>
            ) : (
              <>
                {forgotError && (
                  <div className="alert alert-error">
                    {forgotError}
                  </div>
                )}
                <p className="text-sm text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                  The link expires in 1 hour.
                </p>
                <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="forgotEmail" className="label">
                      Email address
                    </label>
                    <input
                      id="forgotEmail"
                      name="forgotEmail"
                      type="email"
                      autoComplete="email"
                      required
                      className="input"
                      placeholder="Email address"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            )}
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false)
                  setForgotMessage('')
                  setForgotError('')
                }}
                className="tap text-sm text-primary-600 hover:text-primary-500 font-medium"
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : mfaSetup ? (
          /* ===== First-login MFA setup wizard ===== */
          <MfaSetupWizard
            sessionId={sessionId}
            onComplete={async () => {
              await refreshUser()
              navigate('/dashboard')
            }}
            onCancel={() => {
              setMfaSetup(false)
              setSessionId('')
              setError('')
            }}
          />
        ) : !mfaRequired ? (
          /* ===== Normal login form ===== */
          <form className="mt-8 space-y-6" onSubmit={handleLoginSubmit}>
            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="email" className="label">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  // "webauthn" lets the browser surface a saved passkey in this
                  // field's autofill dropdown (conditional UI).
                  autoComplete="email webauthn"
                  required
                  className="input"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="label">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="mt-1 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(email)
                      setShowForgotPassword(true)
                      setError('')
                    }}
                    className="tap text-sm text-primary-600 hover:text-primary-500 font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            {isPasskeySupported() && (
              <div className="relative mt-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 text-gray-500">or</span>
                </div>
              </div>
            )}

            {isPasskeySupported() && (
              <div>
                <button
                  type="button"
                  disabled={passkeyLoading}
                  onClick={handlePasskeyLogin}
                  className="w-full btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passkeyLoading ? 'Waiting for passkey…' : 'Sign in with a passkey'}
                </button>
                <p className="mt-1 text-center text-xs text-gray-500">
                  Use your fingerprint, face, screen lock, or a security key.
                </p>
              </div>
            )}
          </form>
        ) : (
          /* ===== MFA step ===== */
          <form className="mt-8 space-y-6" onSubmit={handleMFASubmit}>
            <div>
              <label htmlFor="mfaCode" className="label">
                Enter the 6-digit code from your authenticator app
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                maxLength={8}
                required
                className="input text-center text-2xl tracking-widest"
                placeholder="••••••"
                value={mfaCode}
                // Accept TOTP digits AND 8-char alphanumeric backup codes
                // (uppercase, may contain - or _). Backend tries TOTP then backup.
                onChange={(e) => setMfaCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500">
                Lost your authenticator? Enter one of your one-time backup codes instead.
              </p>
            </div>

            <div className="flex items-center">
              <input
                id="rememberDevice"
                type="checkbox"
                className="h-6 w-6 text-primary-600 border-gray-300 rounded"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              <label htmlFor="rememberDevice" className="ml-2 text-sm text-gray-700">
                Remember this device
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  setMfaRequired(false)
                  setMfaCode('')
                  setError('')
                }}
                className="flex-1 btn btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || mfaCode.trim().length < 6}
                className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  )
}
